import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { fetchProducts, deleteProduct, bulkDeleteProducts, fetchProductsWithStock, fetchProductsUsingFilter } from './controllers/products-controller';
import BrandController from './controllers/brand-controller';
import SupplierController from './controllers/supplier-controller';
import GenerateLinkModal from './components/GenerateLinkModal'; // Add this import
import StockBreakdownModal from './components/StockBreakdownModal';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import { usePermissionViewModel } from './permissions/viewmodel/permission-viewmodel';
import { isAdmin,isAuthenticated } from '../../login/controller/login-controller';
import JsBarcode from 'jsbarcode';
// Product categories for filtering
const PRODUCT_CATEGORIES = [
  'Toutes les catégories',
  'Électronique',
  'Vêtements',
  'Maison',
  'Accessoires',
  'Sports'
];

const ProductsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('Toutes les catégories');
  const [stockFilter, setStockFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    maxPage: 1
  });

  const [stats, setStats] = useState({
    totalProducts: 0,
    inStock: 0,
    outOfStock: 0
  });

  const [brands, setBrands] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [brandFilter, setBrandFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [filterParams, setFilterParams] = useState(null);
  const { permission,hasPermission } = usePermissionViewModel('produit');

  // First, add a state to track expanded product rows
  const [expandedProducts, setExpandedProducts] = useState([]);

  // Update the getImagePlaceholder function to be more specific
  const getImagePlaceholder = (isVariant = false) => {
    return "https://www.mon-site-bug.fr/uploads/products/default-product.png";
  };

  // Add a new state to track the active filter
  const [activeStockFilter, setActiveStockFilter] = useState('all'); // 'all', 'inStock', 'outOfStock'

  // Add this state for the modal
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);

  // Stock breakdown popup state
  const [stockModal, setStockModal] = useState({ open: false, productId: null, productName: '' });
  const openStockModal = (product) => setStockModal({ open: true, productId: product.id, productName: product.name });
  const closeStockModal = () => setStockModal({ open: false, productId: null, productName: '' });



  useEffect(() => {
    const fetchFiltersData = async () => {
      try {
        setLoadingBrands(true);
        setLoadingSuppliers(true);

        // Fetch brands
        const brandsData = await BrandController.fetchAllBrands();
        setBrands(brandsData || []);
        setLoadingBrands(false);

        // Fetch suppliers
        const suppliersData = await SupplierController.fetchAllSuppliers();
        setSuppliers(suppliersData || []);
        setLoadingSuppliers(false);
      } catch (err) {
        console.error('Error loading filter data:', err);
        setLoadingBrands(false);
        setLoadingSuppliers(false);
      }
    };

    fetchFiltersData();
  }, []);

  // Show a success toast forwarded from another page (e.g. product edit/create).
  useEffect(() => {
    if (location.state?.successMessage) {
      const id = toast.success(location.state.successMessage);
      navigate(location.pathname, { replace: true, state: {} });
      // Dismiss it when leaving this page so it doesn't bleed onto other routes.
      return () => toast.dismiss(id);
    }
  }, [location, navigate]);

  useEffect(() => {
    const getProducts = async () => {
      try {
        setLoading(true);
        let data;

        // Default fetch without filters
        if (activeStockFilter === 'all' && !isFiltering) {
          data = await fetchProducts(pagination.page, pagination.pageSize);
        }
        // Fetch with stock filter only
        else if (activeStockFilter !== 'all' && !isFiltering) {
          const isInStock = activeStockFilter === 'inStock';
          data = await fetchProductsWithStock(pagination.page, pagination.pageSize, isInStock);
        }
        // Fetch with combined filters
        else if (isFiltering && filterParams) {
          data = await fetchProductsUsingFilter({
            page: pagination.page,
            pageSize: pagination.pageSize,
            search: filterParams.search || "",
            brandId: filterParams.brandId,
            supplierId: filterParams.supplierId,
            isInStock: filterParams.isInStock
          });
        }

        // Process response and update state
        // API responses sometimes wrap the payload under a `data` key, or return fields at the root.
        // Normalize to `resp` so we can read pagination fields reliably.
        const resp = data?.data || data || {};
        const productList = resp?.list || data?.list || [];
        setProducts(productList);

        const respPageSize = resp.pageSize || pagination.pageSize;
        const respTotal = resp.total ?? productList.length;
        const respMaxPage = resp.maxPage || Math.ceil(respTotal / respPageSize);

        setPagination({
          page: resp.page || pagination.page,
          pageSize: respPageSize,
          total: respTotal,
          maxPage: respMaxPage
        });

        setStats({
          totalProducts: data.totalProduct || 0,
          inStock: data.totalProductEnStock || 0,
          outOfStock: data.ruptureStock || 0
        });

        setLoading(false);
      } catch (err) {
        console.error('Error loading products:', err);
        setError('Échec du chargement des produits');
        setLoading(false);
      }
    };

    getProducts();
  }, [pagination.page, pagination.pageSize, activeStockFilter, isFiltering, filterParams]);
  const handleStatsCardClick = (filter) => {
    // Reset to page 1 when changing filters
    setPagination(prev => ({ ...prev, page: 1 }));
    setActiveStockFilter(filter);

    // Also update the stock filter dropdown to match
    if (filter === 'inStock') {
      setStockFilter('in-stock');
    } else if (filter === 'outOfStock') {
      setStockFilter('out-of-stock');
    } else {
      setStockFilter('all');
    }
  };

  // Handle sorting
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Print barcode tickets for all variants of a product.
  // Target printer: Honeywell PC42E-T (203 dpi), label paper 2" x 4" = 50.8 x 101.6 mm (portrait).
  const printProductBarcodes = (product) => {
    const variants = (product.productVariants || []).filter(v => v.codeABarre && String(v.codeABarre).trim());
    if (variants.length === 0) {
      alert('Aucune variante avec code-barres pour ce produit');
      return;
    }
    const priceNum = Number(product.prixVenteTTC);
    const priceText = Number.isFinite(priceNum) ? `${priceNum.toFixed(2)} TND` : '';
    // Promotion is defined at the product level (EnPromo + PrixVentePromo).
    const enPromo = Boolean(product.enPromo) && Number(product.prixVentePromo) > 0;
    const promoNum = Number(product.prixVentePromo);
    const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    const ticketsHtml = variants.map(v => {
      const code = String(v.codeABarre);
      let dataUrl = '';
      try {
        const canvas = document.createElement('canvas');
        JsBarcode(canvas, code, {
          format: 'CODE128',
          width: 2,
          height: 95,
          fontSize: 20,
          textMargin: 2,
          fontOptions: 'bold',
          margin: 0,
          displayValue: true,
          background: '#ffffff',
          lineColor: '#000000',
        });
        dataUrl = canvas.toDataURL('image/png');
      } catch (e) {
        console.error('Barcode generation failed for', code, e);
      }
      const vPriceNum = Number(v.prixVenteTTC);
      const vRegularNum = Number.isFinite(vPriceNum) && vPriceNum > 0 ? vPriceNum : priceNum;
      const effectiveNum = enPromo ? promoNum : vRegularNum;
      const vPriceText = Number.isFinite(effectiveNum) && effectiveNum > 0 ? `${effectiveNum.toFixed(2)} TND` : priceText;
      const vOldText = enPromo && Number.isFinite(vRegularNum) && vRegularNum > effectiveNum ? `${vRegularNum.toFixed(2)} TND` : '';
      const refText = String(v.reference || product.reference || '').trim();
      return `
        <div class="ticket">
          <div class="brand">Tendresse Store</div>
          ${refText ? `<div class="reference">${esc(refText)}</div>` : ''}
          ${(v.name || '') ? `<div class="variant">${esc(v.name)}</div>` : ''}
          <div class="name">${esc(product.name || '')}</div>
          <div class="barcode-wrap">
            ${dataUrl
              ? `<img class="barcode" src="${dataUrl}" alt="${esc(code)}"/>`
              : `<div class="barcode-fallback">${esc(code)}</div>`}
          </div>
          <div class="price">${vOldText ? `<span class="price-old">${esc(vOldText)}</span> ` : ''}${esc(vPriceText)}</div>
        </div>
      `;
    }).join('');

    const printHtml = `<!doctype html><html><head><meta charset="utf-8"/>
      <title>Codes-barres - ${esc(product.name || '')}</title>
      <style>
        @page { size: 50mm 40mm; margin: 0; }
        html, body { margin: 0; padding: 0; background: #fff; }
        body { font-family: Arial, 'Helvetica', sans-serif; color: #000;
               -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .ticket {
          width: 50mm; height: 40mm;
          padding: 1.5mm 2mm 1.5mm 2mm;
          box-sizing: border-box;
          display: flex; flex-direction: column;
          align-items: center; justify-content: flex-start;
          page-break-after: always; break-after: page;
          overflow: hidden;
        }
        .ticket:last-child { page-break-after: auto; break-after: auto; }
        .brand { font-size: 11pt; font-weight: 900; text-align: center;
                 line-height: 1; letter-spacing: 1.2px;
                 font-family: 'Georgia', 'Times New Roman', serif;
                 font-style: italic;
                 width: 100%;
                 padding-bottom: 0.6mm;
                 border-bottom: 0.3mm solid #000; }
        .name { font-size: 6.5pt; font-weight: 700; text-align: center;
                line-height: 1.05; margin-top: 0.3mm; max-height: 4mm; overflow: hidden;
                word-break: break-word; }
        .variant { font-size: 8.5pt; font-weight: 900; text-align: center;
                   line-height: 1.05; margin-top: 0.4mm; overflow: visible;
                   word-break: break-word; }
        .reference { font-size: 9.5pt; font-weight: 900; text-align: center;
                     line-height: 1.05; margin-top: 0.6mm; color: #000;
                     overflow: visible; word-break: break-word; }
        .barcode-wrap { width: 100%; display: flex; justify-content: center;
                        align-items: center; margin-top: 0.8mm; }
        .barcode { width: 46mm; height: 17mm; display: block; }
        .barcode-fallback { font-family: 'Courier New', monospace;
                            font-size: 11pt; font-weight: 700; }
        .price { font-size: 12pt; font-weight: 900; text-align: center;
                 margin-top: 1mm; letter-spacing: 0.3px; white-space: nowrap; }
        .price-old { font-size: 8pt; font-weight: 700; text-decoration: line-through;
                     color: #555; margin-right: 1mm; }
      </style></head><body>
      ${ticketsHtml}
      </body></html>`;

    // Print via a hidden iframe (more reliable than window.open, which can
    // leave a blank about:blank popup if the browser blocks document.write).
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(iframe);
    const cleanup = () => { try { document.body.removeChild(iframe); } catch (e) { /* ignore */ } };
    iframe.onload = () => {
      try {
        const w = iframe.contentWindow;
        const imgs = Array.prototype.slice.call(w.document.images);
        const triggerPrint = () => {
          try { w.focus(); w.print(); } catch (e) { console.error(e); }
          w.onafterprint = () => setTimeout(cleanup, 200);
          setTimeout(cleanup, 60000);
        };
        let done = 0;
        const check = () => { if (done >= imgs.length) setTimeout(triggerPrint, 150); };
        if (imgs.length === 0) { setTimeout(triggerPrint, 150); return; }
        imgs.forEach((im) => {
          if (im.complete && im.naturalWidth > 0) { done++; }
          else {
            im.addEventListener('load', () => { done++; check(); });
            im.addEventListener('error', () => { done++; check(); });
          }
        });
        check();
      } catch (e) { console.error(e); cleanup(); }
    };
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(printHtml);
    doc.close();
  };

  // Update the handleDelete function
  const handleDelete = (productId) => {
    setProductToDelete(productId);
    setShowDeleteModal(true);
  };

  // Add a function to confirm deletion
  const confirmDelete = async () => {
    try {
      setIsDeleting(true);
      const res = await deleteProduct(productToDelete);
      if (res.status == 417) {
        toast.error("Erreur lors de la suppression des produits sélectionnés. Produit déjà utilisé.");
        setIsDeleting(false);
        setProductToDelete(null);
      } else {
        setProducts(products.filter(product => !selectedProducts.includes(product.id)));
        setSelectedProducts([]);
      }
    } catch (err) {
      setError('Échec de la suppression du produit');
    } finally {
      setIsDeleting(false);
      setProductToDelete(null);
      setShowDeleteModal(false);
    }
  };
  

  // Handle bulk selection
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedProducts(filteredProducts.map(p => p.id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectProduct = (productId) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    } else {
      setSelectedProducts([...selectedProducts, productId]);
    }
  };

  // Update the handleBulkDelete function
  const handleBulkDelete = () => {
    setShowBulkDeleteModal(true);
  };

  // Add a function to confirm bulk deletion
  const confirmBulkDelete = async () => {
    try {
      setIsDeleting(true);
      const res = await bulkDeleteProducts(selectedProducts);
      console.log('this is the res ', res);
      if(res.status === 417){
        toast.error("Erreur lors de la suppression des produits sélectionnés. Produit déja utilisé.");
      }else{
        setProducts(products.filter(product => !selectedProducts.includes(product.id)));
        setSelectedProducts([]);
      }
      setShowBulkActions(false);
      setShowBulkDeleteModal(false);
    } catch (err) {
      console.error('Error deleting products:', err);
      setError('Échec de la suppression des produits');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle toggling featured status
  const handleToggleFeatured = async (productId) => {
    setProducts(products.map(product =>
      product.id === productId ? { ...product, featured: !product.featured } : product
    ));
  };

  // Add a toggle function
  const toggleProductExpansion = (productId) => {
    if (expandedProducts.includes(productId)) {
      setExpandedProducts(expandedProducts.filter(id => id !== productId));
    } else {
      setExpandedProducts([...expandedProducts, productId]);
    }
  };

  const getNestedValue = (obj, path) => {
    const keys = path.split('.');
    return keys.reduce((o, key) => (o || {})[key], obj);
  };

  // Update the filteredProducts function to remove frontend filtering for search, supplier, and brand
  const filteredProducts = React.useMemo(() => {
    let filtered = [...products];

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        // Handle nested properties or missing values
        const aValue = getNestedValue(a, sortConfig.key) ?? '';
        const bValue = getNestedValue(b, sortConfig.key) ?? '';

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [products, sortConfig]);


  const getSortIndicator = (columnName) => {
    if (sortConfig.key !== columnName) return null;
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  // Function to export data to Excel
  const exportToExcel = () => {
    if (!filteredProducts || filteredProducts.length === 0) {
      alert("Aucun produit à exporter.");
      return;
    }
  
    // Dynamically extract column headers from the first product
    const headers = Object.keys(filteredProducts[0]).reduce((acc, key) => {
      // Map keys to human-readable column names (if needed)
      const columnName = {
        id: "ID",
        name: "Nom",
        reference: "Référence",
        category: "Catégorie",
        prixVenteTTC: "Prix (TND)",
        stock: "Stock",
        featured: "Mis en avant",
        rating: "Évaluation",
        numReviews: "Avis",
        // Add more mappings as needed
      }[key] || key; // Default to the key if no mapping exists
  
      acc[key] = columnName;
      return acc;
    }, {});
  
    // Create a worksheet with the filtered products
    const worksheet = XLSX.utils.json_to_sheet(
      filteredProducts.map((product) => {
        // Map product data to match the headers
        const row = {};
        Object.keys(headers).forEach((key) => {
          row[headers[key]] = product[key] ?? ""; // Use the mapped column name
        });
        return row;
      })
    );
  
    // Set column widths dynamically based on the header length
    const columnWidths = Object.values(headers).map((header) => ({
      wch: Math.max(header.length, 15), // Minimum width of 15
    }));
    worksheet["!cols"] = columnWidths;
  
    // Create a workbook and add the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Produits");
  
    // Generate Excel file and save
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
  
    // Generate date string for filename
    const date = new Date();
    const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  
    // Save file
    saveAs(data, `products-export-${dateString}.xlsx`);
  };
  

  // Add these near the top of your component with other state declarations


  // Add a function to apply filters
  const applyFilters = () => {
    // Convert brandId and supplierId to integers if they're not 'all'
    const brandIdValue = brandFilter !== 'all' ? parseInt(brandFilter) : null;
    const supplierIdValue = supplierFilter !== 'all' ? parseInt(supplierFilter) : null;

    // Determine stock status
    let stockStatus = null;
    if (activeStockFilter === 'inStock') stockStatus = true;
    else if (activeStockFilter === 'outOfStock') stockStatus = false;

    // Save current filter state
    setFilterParams({
      search: searchTerm,
      brandId: brandIdValue,
      supplierId: supplierIdValue,
      isInStock: stockStatus,
      timestamp: Date.now() // To ensure state change even if filters don't change
    });

    // Reset to first page when applying filters
    setPagination(prev => ({ ...prev, page: 1 }));

    // Enable filtering mode
    setIsFiltering(true);
  };

  // Add a function to clear filters
  const clearFilters = () => {
    // Reset filter inputs
    setSearchTerm('');
    setBrandFilter('all');
    setSupplierFilter('all');
    setActiveStockFilter('all');
    setStockFilter('all');

    // Reset to first page
    setPagination(prev => ({ ...prev, page: 1 }));

    // Disable filtering mode
    setIsFiltering(false);
    setFilterParams(null);
  };

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Produits</h1>
          <p className="mt-2 text-sm text-gray-700">
            Gérez votre catalogue de produits, ajoutez de nouveaux produits ou mettez à jour ceux existants.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          {/* Add export button */}
          <button
            onClick={exportToExcel}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
          >
            <svg className="-ml-1 mr-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Exporter vers Excel
          </button>

{   hasPermission('write') &&       <Link
            to="/admin/products/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Ajouter un produit
          </Link>
}        </div>
      </div>

      {/* Add export notice when filters are applied */}
      {(searchTerm || categoryFilter !== 'All Categories' || stockFilter !== 'all') && (
        <div className="mt-2 text-xs text-gray-500">
          Remarque : l'exportation inclura uniquement les produits actuellement filtrés ({filteredProducts.length} produits)
        </div>
      )}

      {/* Stats cards */}
      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {/* Total Products Card */}
        <div
          className={`bg-white overflow-hidden shadow rounded-lg cursor-pointer transition duration-150 ${activeStockFilter === 'all' ? 'ring-2 ring-blue-500' : 'hover:bg-gray-50'}`}
          onClick={() => handleStatsCardClick('all')}
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3 rounded-md bg-blue-100 text-blue-600">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0v10l-8 4m-8-4V7m8 4v10M4 7v10l8-4" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total des produits</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">
                      {stats.totalProducts}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* In Stock Card */}
        <div
          className={`bg-white overflow-hidden shadow rounded-lg cursor-pointer transition duration-150 ${activeStockFilter === 'inStock' ? 'ring-2 ring-green-500' : 'hover:bg-gray-50'}`}
          onClick={() => handleStatsCardClick('inStock')}
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3 rounded-md bg-green-100 text-green-600">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">En stock</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">
                      {stats.inStock}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Out of Stock Card */}
        <div
          className={`bg-white overflow-hidden shadow rounded-lg cursor-pointer transition duration-150 ${activeStockFilter === 'outOfStock' ? 'ring-2 ring-yellow-500' : 'hover:bg-gray-50'}`}
          onClick={() => handleStatsCardClick('outOfStock')}
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3 rounded-md bg-yellow-100 text-yellow-600">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">En rupture de stock</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">
                      {stats.outOfStock}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {showBulkActions && selectedProducts.length > 0 && (
        <div className="bg-gray-50 border-t border-b border-gray-200 py-4 px-4 mt-6 sm:rounded-lg sm:border-l sm:border-r">
          <div className="sm:flex sm:items-center sm:justify-between">
            <div className="text-sm font-medium text-gray-700">
              {selectedProducts.length} {selectedProducts.length === 1 ? 'produit' : 'produits'} sélectionné
            </div>
            <div className="mt-3 sm:mt-0 sm:ml-4 flex space-x-2">
              {/* Only show Generate Link button when exactly one product is selected */}
              {selectedProducts.length === 1 && (
                <button
                  type="button"
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
                  onClick={() => setIsLinkModalOpen(true)}
                >
                  Générer un lien
                </button>
              )}
              <button
                type="button"
                disabled={!hasPermission('delete')}
                className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                onClick={handleBulkDelete}
              >
                Supprimer la sélection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="mt-6 space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm h-[38px]"
                placeholder="Rechercher par nom de produit, référence ou marque..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    onClick={() => setSearchTerm('')}
                    className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  >
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Brand Filter Dropdown */}
          <div>
            <select
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              disabled={loadingBrands}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm rounded-md disabled:bg-gray-100"
            >
              <option value="all">Toutes les marques</option>
              {loadingBrands ? (
                <option disabled>Chargement des marques...</option>
              ) : (
                brands.map(brand => (
                  <option key={brand.id} value={brand.id}>{brand.name}</option>
                ))
              )}
            </select>
          </div>

          {/* Supplier Filter Dropdown */}
          <div>
            <select
              value={supplierFilter}
              onChange={(e) => setSupplierFilter(e.target.value)}
              disabled={loadingSuppliers}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm rounded-md disabled:bg-gray-100"
            >
              <option value="all">Tous les fournisseurs</option>
              {loadingSuppliers ? (
                <option disabled>Chargement des fournisseurs...</option>
              ) : (
                suppliers.map(supplier => (
                  <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                ))
              )}
            </select>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={clearFilters}
            disabled={loading || (!isFiltering && searchTerm === '' && brandFilter === 'all' && supplierFilter === 'all' && activeStockFilter === 'all')}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] disabled:opacity-50"
          >
            <svg className="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Réinitialiser
          </button>
          <button
            onClick={applyFilters}
            disabled={loading}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] disabled:opacity-50"
          >
            {loading ? (
              <svg className="animate-spin -ml-0.5 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
            )}
            Filtrer
          </button>
        </div>

        {/* Active Filters Indicators */}
        {isFiltering && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium text-gray-700">Filtres actifs:</span>
            {searchTerm && (
              <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full flex items-center">
                Recherche: {searchTerm}
              </span>
            )}
            {brandFilter !== 'all' && (
              <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full flex items-center">
                Marque: {brands.find(b => b.id === parseInt(brandFilter))?.name || brandFilter}
              </span>
            )}
            {supplierFilter !== 'all' && (
              <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full flex items-center">
                Fournisseur: {suppliers.find(s => s.id === parseInt(supplierFilter))?.name || supplierFilter}
              </span>
            )}
            {activeStockFilter !== 'all' && (
              <span className={`px-2 py-0.5 rounded-full flex items-center ${activeStockFilter === 'inStock'
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
                }`}>
                Stock: {activeStockFilter === 'inStock' ? 'En stock' : 'En rupture'}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Products Table */}
      {/* Products Table - Enhanced Responsive Design */}
      <div className="mt-8 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
        {loading ? (
          <div className="flex justify-center items-center h-64 bg-white">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#174540]"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12 bg-white">
            <div className="text-red-500 mb-2">{error}</div>
            <button
              onClick={() => window.location.reload()}
              className="text-[#174540] hover:underline"
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            {/* Desktop Table (hidden on small screens) */}
            <div className="hidden md:block">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="relative px-7 sm:w-16 sm:px-6">
                      <input
                        type="checkbox"
                        className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-[#174540] focus:ring-[#174540]"
                        checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                        onChange={(e) => {
                          handleSelectAll(e);
                          setShowBulkActions(e.target.checked);
                        }}
                      />
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expand
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('name')}
                    >
                      Produit{getSortIndicator('name')}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('reference')}
                    >
                      Reference{getSortIndicator('reference')}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    >
                      Marque
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('prixVenteTTC')}
                    >
                      Prix{getSortIndicator('prixVenteTTC')}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('stock')}
                    >
                      Stock{getSortIndicator('stock')}
                    </th>
                    <th scope="col" className="relative px-3 py-3.5">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredProducts.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-10 text-center text-sm text-gray-500">
                        Aucun produit trouvé correspondant à vos critères.
                      </td>
                    </tr>
                  ) : (
                    filteredProducts.map((product) => (
                      <React.Fragment key={product.id}>
                        {/* Main product row */}
                        <tr className={selectedProducts.includes(product.id) ? "bg-gray-50" : "hover:bg-gray-50"}>
                          <td className="relative px-7 sm:w-16 sm:px-6">
                            <input
                              type="checkbox"
                              className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-[#174540] focus:ring-[#174540]"
                              checked={selectedProducts.includes(product.id)}
                              onChange={() => {
                                handleSelectProduct(product.id);
                                if (!showBulkActions) setShowBulkActions(true);
                              }}
                            />
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            {product.productVariants && product.productVariants.length > 0 && (
                              <button
                                onClick={() => toggleProductExpansion(product.id)}
                                className="text-gray-500 hover:text-gray-700"
                              >
                                {expandedProducts.includes(product.id) ? (
                                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                ) : (
                                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 111.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </button>
                            )}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-10 w-10 flex-shrink-0">
                                <img
                                  className="h-10 w-10 rounded object-cover"
                                  src={product.imageUrl || getImagePlaceholder(false)}
                                  alt={product.name}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = getImagePlaceholder(false);
                                  }}
                                />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                <div className="text-sm text-gray-500">ID: {product.id}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {product.reference}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {product.marque?.name || 'N/A'}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {`${product.prixVenteTTC.toFixed(2)} TND`}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => openStockModal(product)}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-[#174540] bg-[#f0f4f4] hover:bg-[#e1eaea] rounded-md"
                            >
                              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                              Voir stock
                            </button>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex space-x-3 justify-end">
                              <button
                                type="button"
                                onClick={() => printProductBarcodes(product)}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-[#174540] hover:bg-[#0d2926] rounded-md"
                                title="Imprimer les codes-barres"
                              >
                                <svg className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h1m3 0h1m3 0h1m3 0h1m3 0h1M4 18h1m3 0h1m3 0h1m3 0h1m3 0h1M4 6v12M9 6v12M14 6v12M19 6v12" />
                                </svg>
                                Codes-barres
                              </button>
{  hasPermission('edit')&&                            <Link
                                to={`/admin/products/${product.id}/edit`}
                                className="text-[#174540] hover:text-[#0d2926]"
                              >
                                Modifier
                              </Link>
} 
{hasPermission('delete')      &&                       <button
                                onClick={() => handleDelete(product.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Supprimer
                              </button>
}                            </div>
                          </td>
                        </tr>

                        {/* Variants rows (shown when expanded) */}
                        {expandedProducts.includes(product.id) && product.productVariants &&
                          product.productVariants.map((variant) => (
                            <tr key={`variant-${variant.id}`} className="bg-gray-50 border-t border-gray-200">
                              <td className="px-7 sm:w-16 sm:px-6"></td>
                              <td className="px-3 py-4"></td>
                              <td className="px-3 py-4 whitespace-nowrap">
                                <div className="flex items-center pl-8">
                                  <div className="h-8 w-8 flex-shrink-0 mr-4">
                                    <img
                                      className="h-8 w-8 rounded object-cover"
                                      src={variant.imageUrl || getImagePlaceholder(true)}
                                      alt={variant.name || `Variant ${variant.reference}`}
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = getImagePlaceholder(true);
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      {variant.name || `Variant ${variant.reference}`}
                                    </div>
                                    <div className="text-sm text-gray-500">ID: {variant.id}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                                {variant.reference}
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                                -
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                -
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap">
                                {/* Stock badge removed — see "Voir stock" button on parent row */}
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                                {/* Variant actions removed as requested */}
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card Layout (visible on small screens) */}
            <div className="block md:hidden">
              {filteredProducts.length === 0 ? (
                <div className="px-6 py-10 text-center text-sm text-gray-500 bg-white">
                  Aucun produit trouvé correspondant à vos critères.
                </div>
              ) : (
                <div className="divide-y divide-gray-200 bg-white">
                  {filteredProducts.map((product) => (
                    <div key={product.id} className="p-4">
                      {/* Product Card Header */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            
                            className="h-4 w-4 rounded border-gray-300 text-[#174540] focus:ring-[#174540] mr-3"
                            checked={selectedProducts.includes(product.id)}
                            onChange={() => {
                              handleSelectProduct(product.id);
                              if (!showBulkActions) setShowBulkActions(true);
                            }}
                          />
                          <div className="h-10 w-10 flex-shrink-0">
                            <img
                              className="h-10 w-10 rounded object-cover"
                              src={product.imageUrl || getImagePlaceholder(false)}
                              alt={product.name}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = getImagePlaceholder(false);
                              }}
                            />
                          </div>
                        </div>
                        {product.productVariants && product.productVariants.length > 0 && (
                          <button
                            onClick={() => toggleProductExpansion(product.id)}
                            className="ml-2 rounded-full p-1 text-gray-500 hover:bg-gray-100"
                          >
                            {expandedProducts.includes(product.id) ? (
                              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 111.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                        )}
                      </div>

                      {/* Product Card Body */}
                      <div className="ml-7">
                        <div className="text-sm font-medium text-gray-900">{product.name}</div>
                        <div className="text-xs text-gray-500 mt-0.5">ID: {product.id}</div>

                        <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                          <div>
                            <span className="font-medium text-gray-500">Référence:</span>
                            <div className="text-gray-900">{product.reference}</div>
                          </div>
                          <div>
                            <span className="font-medium text-gray-500">Marque:</span>
                            <div className="text-gray-900">{product.marque?.name || 'N/A'}</div>
                          </div>
                          <div>
                            <span className="font-medium text-gray-500">Prix:</span>
                            <div className="text-gray-900 font-medium">{`${product.prixVenteTTC.toFixed(2)} TND`}</div>
                          </div>
                          <div>
                            <span className="font-medium text-gray-500">Stock:</span>
                            <div>
                              <button
                                type="button"
                                onClick={() => openStockModal(product)}
                                className="inline-flex items-center px-3 py-1 text-xs font-medium text-[#174540] bg-[#f0f4f4] hover:bg-[#e1eaea] rounded-md"
                              >
                                Voir stock
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="mt-3 flex space-x-2 border-t border-gray-100 pt-3">
                          <button
                            type="button"
                            onClick={() => printProductBarcodes(product)}
                            className="flex-1 flex items-center justify-center rounded-md py-1.5 text-sm font-medium text-white bg-[#174540] hover:bg-[#0d2926]"
                            title="Imprimer les codes-barres"
                          >
                            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h1m3 0h1m3 0h1m3 0h1m3 0h1M4 18h1m3 0h1m3 0h1m3 0h1m3 0h1M4 6v12M9 6v12M14 6v12M19 6v12" />
                            </svg>
                            Codes-barres
                          </button>
{   hasPermission('edit')     &&                   <Link
                            to={`/admin/products/${product.id}/edit`}
                            className="flex-1 flex items-center justify-center rounded-md py-1.5 text-sm font-medium text-[#174540] bg-[#f0f4f4] hover:bg-[#e1eaea]"
                          >
                            <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                            Modifier
                          </Link>
}
{        hasPermission('delete')    &&              <button
                            onClick={() => handleDelete(product.id)}
                            className="flex-1 flex items-center justify-center rounded-md py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                          >
                            <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Supprimer
                          </button>
}                        </div>
                      </div>

                      {/* Variants (Expanded View) */}
                      {expandedProducts.includes(product.id) && product.productVariants && (
                        <div className="mt-3 ml-7 border-t border-gray-100 pt-2">
                          <div className="text-xs font-medium text-gray-500 mb-2">Variantes</div>
                          <div className="space-y-2">
                            {product.productVariants.map((variant) => (
                              <div key={`mobile-variant-${variant.id}`} className="bg-gray-50 rounded-md p-2">
                                <div className="flex items-center">
                                  <div className="h-8 w-8 flex-shrink-0 mr-2">
                                    <img
                                      className="h-8 w-8 rounded object-cover"
                                      src={variant.imageUrl || getImagePlaceholder(true)}
                                      alt={variant.name || `Variant ${variant.reference}`}
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = getImagePlaceholder(true);
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <div className="text-xs font-medium text-gray-900">
                                      {variant.name || `Variant ${variant.reference}`}
                                    </div>
                                    <div className="text-xs text-gray-500">Réf: {variant.reference}</div>
                                    {/* Variant stock badge removed — see "Voir stock" button on parent product */}
                                  </div>
                                  <div className="ml-auto">
                                    {/* Variant actions removed as requested */}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Single Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Supprimer le produit</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Etes-vous sûr de vouloir supprimer ce produit ? Cette action ne peut pas être annulée.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={confirmDelete}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-red-300"
                >
                  {isDeleting ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Suppression...
                    </span>
                  ) : 'Supprimer'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Supprimer {selectedProducts.length} Produits
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Etes-vous sûr de vouloir supprimer les produits sélectionnés ? Cette action ne peut pas être annulée
                        et supprimera définitivement {selectedProducts.length} {selectedProducts.length === 1 ? 'product' : 'products'}.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={confirmBulkDelete}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-red-300"
                >
                  {isDeleting ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Suppression...
                    </span>
                  ) : 'Supprimer tout'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowBulkDeleteModal(false)}
                  disabled={isDeleting}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

  {/* Pagination */}
  {!loading && !error && pagination.total > 0 && (
        <div className="mt-5 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.max(prev.page - 1, 1) }))}
              disabled={pagination.page === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Précédent
            </button>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.page + 1, prev.maxPage) }))}
              disabled={pagination.page === pagination.maxPage}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Suivant
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Affichage de <span className="font-medium">{(pagination.page - 1) * pagination.pageSize + 1}</span> à{' '}
                <span className="font-medium">
                  {Math.min(pagination.page * pagination.pageSize, pagination.total)}
                </span>{' '}
                sur <span className="font-medium">{pagination.total}</span> résultats
              </p>
            </div>
            <div className="flex items-center gap-3">
              <select value={pagination.pageSize} onChange={e => setPagination(prev => ({ ...prev, pageSize: Number(e.target.value), page: 1 }))} className="border border-gray-300 rounded-md text-sm py-1 pl-2 pr-7 focus:ring-[#174540] focus:border-[#174540]">
                {[5, 10, 25, 50].map(s => <option key={s} value={s}>{s} / page</option>)}
              </select>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(prev.page - 1, 1) }))}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                >
                  <span className="sr-only">Précédent</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                  </svg>
                </button>

                {[...Array(pagination.maxPage)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: i + 1 }))}
                    aria-current={pagination.page === i + 1 ? "page" : undefined}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${pagination.page === i + 1
                      ? 'z-10 bg-[#174540] text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#174540]'
                      : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                      }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.page + 1, prev.maxPage) }))}
                  disabled={pagination.page === pagination.maxPage}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                >
                  <span className="sr-only">Suivant</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Generate Link Modal */}
      <GenerateLinkModal
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        selectedProducts={selectedProducts}
      />

      {/* Stock breakdown popup */}
      <StockBreakdownModal
        isOpen={stockModal.open}
        onClose={closeStockModal}
        productId={stockModal.productId}
        productName={stockModal.productName}
      />
    </div>
  );
};

export default ProductsPage;